package br.com.heyagenda.data.local

import br.com.heyagenda.domain.model.User
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

@Serializable
private data class CachedUserDto(
    val uid: String,
    val name: String,
    val email: String,
    val studioName: String? = null,
    val specialty: String? = null,
    val photoUrl: String? = null,
    val slug: String? = null,
    val themeAccentColor: String? = null,
    val instagramHandle: String? = null,
    val address: String? = null,
    val announcementBanner: String? = null,
    val totalVisits: Long = 0L,
    val phone: String? = null,
    val subscriptionTier: String? = "free",
    val subscriptionBillingCycle: String? = "annual",
    val subscriptionExpiresAt: Long? = null,
    val isFounderPromo: Boolean = true,
    val monthlyAppointmentsCount: Int = 0
)

interface UserLocalDataSource {
    fun saveUser(user: User)
    fun getUser(): User?
    fun clearUser()
}

class UserLocalDataSourceImpl : UserLocalDataSource {

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    private var inMemoryUser: User? = null

    override fun saveUser(user: User) {
        val dto = CachedUserDto(
            uid = user.uid,
            name = user.name,
            email = user.email,
            studioName = user.studioName,
            specialty = user.specialty,
            photoUrl = user.photoUrl,
            slug = user.slug,
            themeAccentColor = user.themeAccentColor,
            instagramHandle = user.instagramHandle,
            address = user.address,
            announcementBanner = user.announcementBanner,
            totalVisits = user.totalVisits,
            phone = user.phone,
            subscriptionTier = user.subscriptionTier,
            subscriptionBillingCycle = user.subscriptionBillingCycle,
            subscriptionExpiresAt = user.subscriptionExpiresAt,
            isFounderPromo = user.isFounderPromo,
            monthlyAppointmentsCount = user.monthlyAppointmentsCount
        )
        inMemoryUser = user
        try {
            persistentUserJson = json.encodeToString(dto)
        } catch (e: Throwable) {
            // Ignore serialization error
        }
    }

    override fun getUser(): User? {
        if (inMemoryUser != null) return inMemoryUser

        val jsonStr = persistentUserJson ?: return null
        return try {
            val dto = json.decodeFromString<CachedUserDto>(jsonStr)
            val user = User(
                uid = dto.uid,
                name = dto.name,
                email = dto.email,
                studioName = dto.studioName,
                specialty = dto.specialty,
                photoUrl = dto.photoUrl,
                slug = dto.slug,
                themeAccentColor = dto.themeAccentColor,
                instagramHandle = dto.instagramHandle,
                address = dto.address,
                announcementBanner = dto.announcementBanner,
                totalVisits = dto.totalVisits,
                phone = dto.phone,
                subscriptionTier = dto.subscriptionTier ?: "free",
                subscriptionBillingCycle = dto.subscriptionBillingCycle ?: "annual",
                subscriptionExpiresAt = dto.subscriptionExpiresAt,
                isFounderPromo = dto.isFounderPromo,
                monthlyAppointmentsCount = dto.monthlyAppointmentsCount
            )
            inMemoryUser = user
            user
        } catch (e: Throwable) {
            null
        }
    }

    override fun clearUser() {
        inMemoryUser = null
        persistentUserJson = null
    }

    companion object {
        private var inMemoryUser: User? = null
        private var persistentUserJson: String? = null
    }
}
